// The superuser's edit dialog for one extension: the admin toggle and, for an
// extension with an endpoint, its URL. A required extension that is off locks
// the toggle, and switching off an extension others need says so before the
// change is saved (ADR-0051).
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  ExtensionSwitchField,
  type ExtensionSwitchNotice,
} from '@/components/extensions/ExtensionSwitchField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { EXTENSIONS_QUERY_KEY } from '@/hooks/use-extensions'
import { type Extension, type ExtensionUpdate, extensionsApi } from '@/services/api/extensions'
import {
  enabledDependents,
  extensionName,
  extensionNames,
  switchLock,
  updateErrorMessage,
} from './extension-copy'

export const extensionHealthKey = (id: string) => ['extensions', id, 'health'] as const

interface EditExtensionDialogProps {
  ext: Extension
  /** Every extension, for what this one requires and what requires it. */
  extensions: readonly Extension[]
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EditExtensionDialog({
  ext,
  extensions,
  open,
  onOpenChange,
}: EditExtensionDialogProps) {
  const { t } = useTranslation()
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('extensions.edit_dialog.title', { name: extensionName(t, ext) })}
          </DialogTitle>
        </DialogHeader>
        {/* Mounted with the content, so every opening starts from the saved state. */}
        <EditExtensionForm ext={ext} extensions={extensions} onDone={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  )
}

function useUpdateExtension(ext: Extension, extensions: readonly Extension[], onDone: () => void) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: ExtensionUpdate) => extensionsApi.patch(ext.id, data),
    onSuccess: () => {
      // Every extension: switching this one can switch its dependents too.
      void queryClient.invalidateQueries({ queryKey: EXTENSIONS_QUERY_KEY })
      onDone()
    },
    onError: error => toast.error(updateErrorMessage(t, error, extensions)),
  })
}

/** Under the switch: why it cannot move, or which extensions switching it off takes along. */
function switchNotice(
  t: TFunction,
  {
    ext,
    extensions,
    enabled,
  }: { ext: Extension; extensions: readonly Extension[]; enabled: boolean }
): ExtensionSwitchNotice | null {
  const lock = switchLock(t, ext, extensions)
  if (lock) return { text: lock, locks: true }
  const dependents = ext.enabled && !enabled ? enabledDependents(ext, extensions) : []
  if (dependents.length === 0) return null
  const names = extensionNames(
    t,
    dependents.map(dependent => dependent.id),
    extensions
  )
  return {
    text: t('extensions.edit_dialog.dependentsOff', { name: extensionName(t, ext), names }),
    locks: false,
  }
}

function EditExtensionForm({
  ext,
  extensions,
  onDone,
}: {
  ext: Extension
  extensions: readonly Extension[]
  onDone: () => void
}) {
  const { t } = useTranslation()
  // Built-in extensions (no default_url) have no endpoint — the dialog is just the toggle.
  const hasEndpoint = ext.default_url != null
  const [enabled, setEnabled] = useState(ext.enabled)
  const [url, setUrl] = useState(ext.url && ext.url !== ext.default_url ? ext.url : '')
  const mutation = useUpdateExtension(ext, extensions, onDone)
  const notice = switchNotice(t, { ext, extensions, enabled })
  // A locked switch is never written: the stored toggle survives until the lock lifts.
  const changes: ExtensionUpdate = {
    ...(notice?.locks ? {} : { enabled }),
    ...(hasEndpoint ? { url: url.trim() || null } : {}),
  }
  const confirmsDependents = notice !== null && !notice.locks
  return (
    <>
      <div className="space-y-4 py-2">
        <ExtensionSwitchField
          inputId={`ext-${ext.id}-enabled`}
          checked={enabled}
          onCheckedChange={setEnabled}
          notice={notice}
        />
        {hasEndpoint && <EndpointField ext={ext} url={url} onUrlChange={setUrl} />}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onDone}>
          {t('extensions.cancel')}
        </Button>
        <Button
          variant={confirmsDependents ? 'destructive' : 'default'}
          onClick={() => mutation.mutate(changes)}
          disabled={mutation.isPending || Object.keys(changes).length === 0}
        >
          {confirmsDependents ? t('extensions.edit_dialog.confirmSwitchOff') : t('extensions.save')}
        </Button>
      </DialogFooter>
    </>
  )
}

function EndpointField({
  ext,
  url,
  onUrlChange,
}: {
  ext: Extension
  url: string
  onUrlChange: (url: string) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="space-y-1.5">
      <Label htmlFor={`ext-${ext.id}-url`} className="text-sm">
        {t('extensions.edit_dialog.url')}
      </Label>
      <Input
        id={`ext-${ext.id}-url`}
        type="url"
        value={url}
        onChange={e => onUrlChange(e.target.value)}
        placeholder={t('extensions.edit_dialog.urlPlaceholder')}
      />
      <p className="text-xs text-muted-foreground">
        {t('extensions.defaultUrl')}:{' '}
        <span className="font-mono max-md:break-all">{ext.default_url}</span>
      </p>
    </div>
  )
}
